import * as React from 'react'

import {
  BoldIcon,
  CameraIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  MinusIcon,
  PaperclipIcon,
  UnderlineIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { Quill } from 'react-quill-new'

import { IAttachment, IUploadLog, UploadingType } from '~/client/graph'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { FileUploadingStore } from '../../../stores/domain/FileUploading.store'
import InitialState from '../../../stores/InitialState'
import IAttachmentAdditionalInfo from '../../../types/IAttachmentAdditionalInfo'
import formatBytes from '../../../utils/formatBytes'

import './TextEditorToolbar.scss'

const icons = Quill.import('ui/icons') as any
icons.image = null
icons.bold = null
icons.italic = null
icons.link = null
icons.list = null
icons.underline = null

interface IProps {
  id: string

  fileUploadingStore?: FileUploadingStore
  isVertical?: boolean
  isCompact?: boolean
  addAttachment(attachment: IAttachment)
  state?: InitialState

  additionalInfo?: IAttachmentAdditionalInfo
  addToUpdateLogList?: (log: IUploadLog) => void
}

@inject('fileUploadingStore', 'state')
@observer
export default class TextEditorToolbar extends React.Component<IProps> {
  public render() {
    const { isVertical, isCompact } = this.props
    const itemClassName = `no-grow ${isVertical ? 'py12' : 'px24'} col y-center`

    return (
      <div
        id={this.props.id}
        className={classList({
          row: !isVertical,
          col: isVertical,
          'overflow-x-auto': isCompact,
        })}
      >
        <div className={itemClassName}>
          <button className="ql-image">
            <CameraIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <FilePicker
            accept=".pdf"
            aria-label={Localization.translator.attachFile}
            className="attachment-upload-button"
            onPick={this.uploadAttachment}
          >
            <PaperclipIcon size={24} />
          </FilePicker>
        </div>
        <div className={itemClassName}>
          <button className="ql-bold">
            <BoldIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-custom-header" value="1">
            <Heading1Icon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-custom-header" value="2">
            <Heading2Icon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-custom-header" value="3">
            <Heading3Icon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-underline">
            <UnderlineIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-list" value="bullet">
            <ListIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-list" value="ordered">
            <ListOrderedIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-hr">
            <MinusIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-link">
            <LinkIcon size={24} className="tw:text-muted-foreground" />
          </button>
        </div>
        <div className={itemClassName}>
          <button className="ql-video" />
        </div>
      </div>
    )
  }

  private uploadAttachment = async ([file]: File[]) => {
    const { additionalInfo } = this.props

    const [result] = await this.props.fileUploadingStore.uploadFile(
      file,
      UploadingType.Pdf,
      this.props.state.activeProject.id,
      file.name,
      false,
      additionalInfo?.sourceEntityId,
      additionalInfo?.sourceEntityType,
      additionalInfo?.locationIds,
      additionalInfo?.sourceEntityCode,
      additionalInfo?.sourceEntityTypeName,
      additionalInfo?.locationName,
    )

    if (!result) {
      return
    }

    this.props.addToUpdateLogList?.(result.uploadLog)

    this.props.addAttachment({
      fileName: file.name,
      size: formatBytes(file.size),
      url: result.fileURL,
    })
  }
}
